import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import axios from "axios";
import {
  Calculator, LogOut, Settings, Loader2, Save, X, CalendarDays,
  Settings as SettingsIcon, Lightbulb, Sliders,
  Share2, Copy, ExternalLink, CheckCircle, Euro, ArrowDownUp,
  Users, ShieldAlert, FileText, History, Mail, Trophy,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import UISettingsDialog from "@/components/UISettingsDialog";
import ProfileDialog from "@/components/ProfileDialog";
import { NotificationsBell } from "@/components/NotificationsBell";
import { toast } from "sonner";
import SalarySuggestions from "@/components/simulator/SalarySuggestions";
import AnnualSimulator from "@/components/simulator/AnnualSimulator";
import {
  BusinessTypeWidget, ConsultantWidget, RegimeWidget, ProposalWidget,
  ProjectWidget, FiscalWidget,
  CombinedBenefitsWidget, EmptyBenefitsWidget, EmptyPermBenefitsWidget,
  ScoreWidget, CompanyResultsWidget, EmployeeResultsWidget, PremiumWidget,
  BusinessSnapshotWidget,
  CashflowChartWidget,
  ShareWidget,
  ManualPremiumWidget,
} from "@/components/simulator/WidgetComponents";
import { useSimulatorForm } from "@/hooks/useSimulatorForm";
import { useSimulatorData } from "@/hooks/useSimulatorData";
import { useSimulatorTemplates } from "@/hooks/useSimulatorTemplates";
import { useSimulatorShare } from "@/hooks/useSimulatorShare";
import { useSimulatorCalculation } from "@/hooks/useSimulatorCalculation";
import { usePermissions } from "@/hooks/usePermissions";
import ProposalEditor from "@/components/ProposalEditor";
import ComparisonView from "@/components/ComparisonView";
import SimulatorHistory from "@/components/simulator/SimulatorHistory";
import { DraftIndicator } from "@/components/simulator/DraftIndicator";
import SimulatorPresets, { PresetSelector, useSimulatorPreset, useEffectiveLayout } from "@/components/simulator/SimulatorPresets";
import LayoutBuilder from "@/components/simulator/LayoutBuilder";
import { fetchBusinessSettings } from "@/hooks/useBusinessSettings";

const SimulatorPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders, isAdmin } = useAuth();
  const [showUISettings, setShowUISettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showProposalEditor, setShowProposalEditor] = useState(false);
  const [activeTab, setActiveTab] = useState("simulator");

  // Custom hooks
  const form = useSimulatorForm();
  const data = useSimulatorData(getAuthHeaders, form.setFormData);

  // Derive current view from business_type + regime_type for per-view layouts
  const [preset, setPreset] = useSimulatorPreset(getAuthHeaders, "full");
  // View = business_type × regime combo (drives per-view custom layouts)
  const currentView = (() => {
    if (form.formData?.business_type === "perm") return "perm";
    const r = form.formData?.regime_type;
    if (r === "green_receipts") return "outsourcing_green_receipts";
    if (r === "b2b") return "outsourcing_b2b";
    return "outsourcing_contract";
  })();
  const effLayout = useEffectiveLayout(getAuthHeaders, currentView);
  const [showLayoutBuilder, setShowLayoutBuilder] = useState(false);

  // Auto-switch business_type/regime_type when the current view is not allowed
  // for the user's group (admins always have all views allowed).
  const [viewAutoSwitched, setViewAutoSwitched] = useState(false);
  useEffect(() => {
    const allowed = effLayout.allowed_views;
    if (!Array.isArray(allowed) || allowed.length === 0) return;
    if (viewAutoSwitched) return;
    if (allowed.includes(currentView)) return;
    // Pick the first allowed view and update form accordingly
    const target = allowed[0];
    const patch = (() => {
      if (target === "perm") return { business_type: "perm", regime_type: "contract", include_iht: false };
      if (target === "outsourcing_green_receipts") return { business_type: "outsourcing", regime_type: "green_receipts", include_iht: true };
      if (target === "outsourcing_b2b") return { business_type: "outsourcing", regime_type: "b2b", include_iht: true };
      return { business_type: "outsourcing", regime_type: "contract", include_iht: true };
    })();
    form.setFormData((prev) => ({ ...prev, ...patch }));
    setViewAutoSwitched(true);
  }, [effLayout.allowed_views, currentView, viewAutoSwitched, form]);
  const tpl = useSimulatorTemplates(getAuthHeaders, data.templates, data.setTemplates, form.formData, form.setFormData);
  const share = useSimulatorShare(getAuthHeaders, form.formData, null, form.consultantName, form.consultantNif, form.consultantBirthDate, form.consultantEmail, form.consultantFunction);
  const calc = useSimulatorCalculation(form.formData, form.debouncedFormData, getAuthHeaders, form.reverseMode, form.desiredNetMonthly, form.debouncedDesiredNet, user?.email, form.consultantName, form.consultantNif, form.consultantBirthDate, form.consultantFunction, form.consultantEmail);
  const { can } = usePermissions(getAuthHeaders, user);
  const [marginColors, setMarginColors] = useState(null);
  const [snapshotColors, setSnapshotColors] = useState(null);
  const [approvalMatch, setApprovalMatch] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/margin-colors`, { headers: getAuthHeaders() })
      .then(res => setMarginColors(res.data))
      .catch(() => {});
    // Reutiliza cache partilhado (evita duplicar GETs com ThemeLoader e AgencySelector)
    fetchBusinessSettings()
      .then((d) => setSnapshotColors(d?.snapshot_colors || null))
      .catch(() => {});
  }, []);

  // Quando as benefit settings chegam da API, sincroniza o valor por defeito do
  // Seguro de Saúde para o configurado no backoffice. Só corre uma vez por carregamento.
  const [healthSynced, setHealthSynced] = useState(false);
  useEffect(() => {
    if (healthSynced) return;
    const bsCost = data.benefitSettings?.health_insurance_cost;
    if (bsCost == null) return;
    // Apenas atualiza se o user ainda não definiu idade (caso contrário o escalão por idade prevalece)
    if (form.formData?.consultant_age != null) { setHealthSynced(true); return; }
    form.setFormData(prev => ({ ...prev, health_insurance_cost: parseFloat(bsCost) }));
    setHealthSynced(true);
  }, [data.benefitSettings, healthSynced]);

  // Auto-derive consultant_age from birth_date and auto-fill health insurance value
  // from configured age tiers. Runs whenever birth date changes.
  useEffect(() => {
    if (!form.consultantBirthDate) return;
    const birth = new Date(form.consultantBirthDate);
    if (Number.isNaN(birth.getTime())) return;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    if (age < 0 || age > 120) return;

    const bs = data.benefitSettings || {};
    const tierCostFor = (a) => {
      if (a < 25) return parseFloat(bs.health_age_below_25 ?? bs.health_age_25_35 ?? 44);
      if (a <= 35) return parseFloat(bs.health_age_25_35 ?? 44);
      if (a <= 50) return parseFloat(bs.health_age_36_50 ?? 55);
      if (a <= 64) return parseFloat(bs.health_age_51_64 ?? 92);
      return parseFloat(bs.health_age_above_64 ?? bs.health_age_51_64 ?? 92);
    };

    form.setFormData(prev => ({
      ...prev,
      consultant_age: age,
      health_insurance_cost: tierCostFor(age),
      include_health_insurance: true,
    }));
  }, [form.consultantBirthDate, data.benefitSettings]);

  // Check if current margin requires business approval
  useEffect(() => {
    const m = calc.result?.margin_percentage;
    if (m === undefined || m === null) { setApprovalMatch(null); return; }
    const ctrl = new AbortController();
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/approval-categories/match`, {
      params: { margin: m },
      headers: getAuthHeaders(),
      signal: ctrl.signal,
    })
      .then(res => setApprovalMatch(res.data?.required ? res.data.category : null))
      .catch(() => setApprovalMatch(null));
    return () => ctrl.abort();
  }, [calc.result?.margin_percentage]);

  // Re-wire share with live results
  const shareWithResults = useSimulatorShare(getAuthHeaders, form.formData, calc.results, form.consultantName, form.consultantNif, form.consultantBirthDate, form.consultantEmail, form.consultantFunction);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Sessão terminada");
  };

  const handleSimulate = () => {
    if (form.reverseMode) {
      calc.calculateReverse(false);
    } else {
      calc.calculateSimulation(false);
    }
  };

  const { formData, setFormData } = form;
  const { result, results } = calc;

  return (
    <div className="min-h-screen bg-background font-sans" data-testid="simulator-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-[1800px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-3 py-1.5">
              <img src="/evolutt_logo_white.png" alt="Evolutt" className="h-10 w-auto" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowProfile(true)}
              className="text-sm text-muted-foreground hover:text-brand-700 transition-colors cursor-pointer"
              title="Editar o meu perfil"
              data-testid="open-profile-btn"
            >
              Olá, <span className="font-medium text-foreground">{user?.name}</span>
            </button>
            {isAdmin && (
              <Link to="/admin">
                <Button variant="outline" size="sm" data-testid="admin-link">
                  <Settings className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => setShowUISettings(true)} title="Configurações de UI" data-testid="ui-settings-btn">
                <SettingsIcon className="w-4 h-4" />
              </Button>
            )}
            <NotificationsBell />
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <UISettingsDialog open={showUISettings} onOpenChange={setShowUISettings} />
      <ProfileDialog open={showProfile} onOpenChange={setShowProfile} />

      {/* Save Template Dialog */}
      {tpl.showSaveTemplate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96 max-w-[90vw]">
            <h3 className="text-lg font-semibold mb-4">Guardar como Template</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Nome *</label>
                <Input value={tpl.newTemplateName} onChange={(e) => tpl.setNewTemplateName(e.target.value)} placeholder="Ex: Senior Developer" className="mt-1" data-testid="template-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Descrição</label>
                <Input value={tpl.newTemplateDesc} onChange={(e) => tpl.setNewTemplateDesc(e.target.value)} placeholder="Descrição opcional" className="mt-1" />
              </div>
              <div className="text-xs text-muted-foreground bg-slate-50 p-2 rounded">
                Tipo: {formData.business_type === "perm" ? "Perm" : "Outsourcing"} | Regime: {formData.regime_type === "contract" ? "Contrato" : formData.regime_type === "green_receipts" ? "Recibos" : "B2B"}
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={tpl.saveAsTemplate} className="flex-1" data-testid="confirm-save-template">Guardar</Button>
                <Button variant="outline" onClick={() => { tpl.setShowSaveTemplate(false); tpl.setNewTemplateName(""); tpl.setNewTemplateDesc(""); }} className="flex-1">Cancelar</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="mx-auto px-6 max-w-[1800px] py-8">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <TabsList className={`grid max-w-3xl bg-transparent p-0 gap-2 ${can("view_history") ? "grid-cols-5" : "grid-cols-4"}`}>
                <TabsTrigger value="simulator" className="flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Simulador
                </TabsTrigger>
                {can("view_annual_view") && formData.regime_type !== "green_receipts" && formData.regime_type !== "b2b" && (
                  <TabsTrigger value="annual" className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />
                    Anual
                  </TabsTrigger>
                )}
                {/* Comparação e Sugestões desactivadas (cálculos incorretos — a rever) */}
                {can("view_history") && (
                  <TabsTrigger value="history" className="flex items-center gap-2" data-testid="history-tab">
                    <History className="w-4 h-4" />
                    Histórico
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Template Selector — escondido nesta fase
              {data.templates.length > 0 ? (
                <Select onValueChange={(v) => {
                  if (v === "__save__") { tpl.setShowSaveTemplate(true); return; }
                  const t = data.templates.find(t => t.id === v);
                  if (t) tpl.applyTemplate(t);
                }}>
                  <SelectTrigger className="h-8 w-48 text-xs" data-testid="template-selector">
                    <SelectValue placeholder="Templates..." />
                  </SelectTrigger>
                  <SelectContent>
                    {data.templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        <div className="flex items-center justify-between w-full gap-2">
                          <span>{t.name}</span>
                          <span className="text-[10px] text-muted-foreground">{t.regime_type === "contract" ? "Contrato" : t.regime_type === "green_receipts" ? "Recibos" : t.regime_type === "b2b" ? "B2B" : t.business_type === "perm" ? "Perm" : ""}</span>
                        </div>
                      </SelectItem>
                    ))}
                    <SelectItem value="__save__">
                      <span className="text-primary font-medium">+ Guardar como Template</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => tpl.setShowSaveTemplate(true)} data-testid="save-template-btn">
                  + Template
                </Button>
              )}
              */}
            </div>

            {/* Simulate Button + Reverse Toggle + Score */}
            {(activeTab === "simulator" || activeTab === "annual") && (
              <div className="flex items-center gap-3">
                {/* Reverse Mode Toggle — HIDDEN nesta fase
                <button
                  onClick={() => form.setReverseMode(!form.reverseMode)}
                  className={`h-10 px-3 rounded-lg flex items-center gap-2 text-xs font-medium border-2 transition-all ${
                    form.reverseMode
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                  }`}
                  data-testid="reverse-mode-toggle"
                  title={form.reverseMode ? "Modo Inverso Ativo: Líquido → Bruto" : "Ativar Calculadora Inversa"}
                >
                  <ArrowDownUp className="w-4 h-4" />
                  Inverso
                </button>
                */}

                {/* Reverse Mode Input — só visível se modo inverso ativo (mantido para compat) */}
                {form.reverseMode && (
                  <div className="relative">
                    <Euro className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-indigo-400" />
                    <Input
                      type="number"
                      placeholder="Líquido desejado"
                      value={form.desiredNetMonthly}
                      onChange={(e) => form.setDesiredNetMonthly(e.target.value)}
                      className="h-10 w-44 pl-8 text-sm border-indigo-300 focus:border-indigo-500"
                      data-testid="desired-net-input"
                    />
                  </div>
                )}

                {/* Candidatos — agora ao lado do Simular */}
                {isAdmin && (
                  <Link to="/candidates">
                    <Button
                      variant="outline"
                      className="h-10 px-4 border-teal-200 text-teal-700 hover:bg-teal-50"
                      data-testid="candidates-link"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Candidatos
                    </Button>
                  </Link>
                )}

                {/* Limpar — agora ao lado do Simular */}
                {isAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (window.confirm("Limpar todos os campos do simulador?")) {
                        form.resetAll();
                        calc.setResults(null);
                      }
                    }}
                    data-testid="clear-simulator-btn"
                    className="h-10 px-4 border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Limpar
                  </Button>
                )}

                <Button
                  onClick={handleSimulate}
                  disabled={calc.isCalculating}
                  className="h-10 px-6 bg-white border-2 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5 font-semibold"
                  data-testid="simulate-btn"
                >
                  {calc.isCalculating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin text-[var(--brand-primary)]" />A calcular...</>
                  ) : (
                    <><Calculator className="w-4 h-4 mr-2 text-[var(--brand-primary)]" />{form.reverseMode ? "Calcular Bruto" : "Simular"}</>
                  )}
                </Button>

                {/* Save Proposal — icon only, ao lado do Simular (sempre visível) */}
                {(can("view_proposals") || can("export_pdf")) && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 border-2 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5 disabled:opacity-40"
                    onClick={shareWithResults.handleSaveAndShare}
                    disabled={!result}
                    title={result ? "Guardar Proposta" : "Faça primeiro uma simulação"}
                    data-testid="save-proposal-icon-btn"
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                )}
                {/* Generate PDF — icon only, ao lado do Simular (sempre visível) */}
                {can("export_pdf") && (
                  <Button
                    size="icon"
                    className="h-10 w-10 bg-[var(--brand-primary)] hover:bg-[#143838] text-white disabled:opacity-40"
                    onClick={() => setShowProposalEditor(true)}
                    disabled={!result}
                    title={result ? "Gerar Proposta PDF" : "Faça primeiro uma simulação"}
                    data-testid="generate-proposal-icon-btn"
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                )}

                {/* Inline Score */}
                {result && can("view_margin") && (() => {
                  const proposta = Number(results?.business_decision?.premium?.premium_value) || 0;
                  const bonusFixo = Number(results?.business_decision?.fixed_bonus?.bonus_value) || 0;
                  const bonusPos = Number(results?.business_decision?.bonus_negocio_positivo?.fixed) || 0;
                  const manual = Number(result?.manual_premium_value) || 0;
                  const premioTotal = proposta + bonusFixo + bonusPos + manual;
                  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v);
                  const permFeePct = results?.business_decision?.fixed_perm_fee?.fee_percentage;
                  return (
                  <div className={`h-10 px-4 rounded-lg flex items-center gap-2 font-bold text-sm ${
                    result.score_color === "green" ? "bg-brand-500 text-white" :
                    result.score_color === "lime" ? "bg-lime-400 text-slate-900" :
                    result.score_color === "yellow" ? "bg-yellow-500 text-white" :
                    result.score_color === "orange" ? "bg-orange-500 text-white" :
                    "bg-red-500 text-white"
                  }`} data-testid="inline-score">
                    <span>{result.margin_percentage?.toFixed(1)}%</span>
                    <span className="text-xs font-normal opacity-80">{result.score_label}</span>
                    {can("view_premium") && premioTotal > 0 && (
                      <span
                        className="text-xs font-semibold ml-1 bg-white/25 px-2 py-0.5 rounded flex items-center gap-1"
                        title={`Prémio total: proposta ${fmt(proposta)} + bónus ${fmt(bonusFixo + bonusPos)} + manual ${fmt(manual)}`}
                        data-testid="inline-premium-badge"
                      >
                        <Trophy className="w-3 h-3" />
                        {fmt(premioTotal)}
                      </span>
                    )}
                    {can("view_premium") && permFeePct != null && (
                      <span className="text-xs font-semibold ml-1 bg-white/20 px-2 py-0.5 rounded" title="Fee Perm fixo por escalão" data-testid="perm-fee-badge">
                        {permFeePct}%
                      </span>
                    )}
                  </div>
                  );
                })()}

                {/* Approval Required Badge */}
                {result && can("view_margin") && approvalMatch && (
                  <div
                    className="h-10 px-3 rounded-lg flex items-center gap-2 bg-orange-100 border-2 border-orange-300 text-orange-800 text-xs font-semibold"
                    title={`Aprovadores: ${approvalMatch.emails?.join(", ")}`}
                    data-testid="approval-required-badge"
                  >
                    <ShieldAlert className="w-4 h-4" />
                    <span>Requer aprovação:</span>
                    <span className="font-bold">{approvalMatch.name}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </Tabs>

        {/* Reverse Calculation Result Banner */}
        {form.reverseMode && results?.reverse_calculation && (
          <div className="mb-4 p-4 bg-indigo-50 border-2 border-indigo-200 rounded-lg" data-testid="reverse-result-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ArrowDownUp className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="font-semibold text-indigo-800">Resultado da Calculadora Inversa</p>
                  <p className="text-sm text-indigo-600">
                    Para obter <span className="font-bold">{new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(results.reverse_calculation.desired_net_monthly)}</span> líquidos/mês,
                    o bruto mensal necessário é <span className="font-bold text-indigo-900">{new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(results.reverse_calculation.calculated_gross_monthly)}</span>
                    <span className="text-xs ml-2 opacity-70">(precisão: ±{new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(results.reverse_calculation.accuracy)})</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Annual Tab */}
        {activeTab === "annual" && can("view_annual_view") && (
          <div className="max-w-6xl mx-auto">
            <AnnualSimulator result={result} formData={formData} />
          </div>
        )}

        {/* Suggestions Tab */}
        {activeTab === "suggestions" && (
          <div className="max-w-4xl mx-auto">
            <SalarySuggestions
              getAuthHeaders={getAuthHeaders}
              onSelectSuggestion={(suggestion) => {
                setFormData(prev => ({
                  ...prev,
                  business_type: "outsourcing",
                  regime_type: "contract",
                  gross_value: suggestion.grossSalary.toString(),
                  value_type: "monthly",
                }));
                setActiveTab("simulator");
                toast.success(`Salário de ${suggestion.grossSalary}€ aplicado ao simulador`);
              }}
            />
          </div>
        )}

        {/* Comparison Tab */}
        {activeTab === "comparison" && (
          <div className="max-w-5xl mx-auto">
            <ComparisonView getAuthHeaders={getAuthHeaders} />
          </div>
        )}

        {activeTab === "history" && can("view_history") && (
          <div className="max-w-7xl mx-auto">
            <SimulatorHistory />
          </div>
        )}

        {/* Simulator Tab */}
        {activeTab === "simulator" && (
          <div>
            {/* Draft indicator + Preset selector */}
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <DraftIndicator
                  savedAt={form.savedAt}
                  hasDraft={form.hasDraft}
                  onClear={() => {
                    if (window.confirm("Limpar todos os campos do simulador?")) {
                      form.resetAll();
                      calc.setResults(null);
                    }
                  }}
                />
                <PresetSelector preset={preset} onChange={setPreset} />
                {effLayout.allow_customization && (
                  <Button variant="outline" size="sm" onClick={() => setShowLayoutBuilder(true)} data-testid="open-layout-builder-btn" className="gap-1 h-8">
                    <Sliders className="w-4 h-4" />
                    <span className="text-xs">Personalizar</span>
                  </Button>
                )}
              </div>
              {/* Manual Premium — admin-only compact block, aligned to the right */}
              {isAdmin && (
                <div className="max-w-sm">
                  <div className="bg-white rounded-lg border-2 border-rose-200 shadow-sm">
                    <ManualPremiumWidget formData={formData} setFormData={setFormData} />
                  </div>
                </div>
              )}
            </div>
            <SimulatorPresets
              preset={preset}
              isAdmin={isAdmin}
              customLayout={effLayout.source === "user" || effLayout.source === "group" ? { rows: effLayout.rows, custom_widgets: effLayout.custom_widgets } : null}
              contents={{
                form_business_type: <BusinessTypeWidget formData={formData} setFormData={setFormData} allowedViews={effLayout.allowed_views} />,
                form_consultant: <ConsultantWidget
                  consultantName={form.consultantName} setConsultantName={form.setConsultantName}
                  consultantBirthDate={form.consultantBirthDate} setConsultantBirthDate={form.setConsultantBirthDate}
                  consultantNif={form.consultantNif} setConsultantNif={form.setConsultantNif}
                  consultantEmail={form.consultantEmail} setConsultantEmail={form.setConsultantEmail}
                  consultantFunction={form.consultantFunction} setConsultantFunction={form.setConsultantFunction}
                  jobFunctions={data.jobFunctions}
                  formData={formData} setFormData={setFormData}
                />,
                form_regime: <RegimeWidget formData={formData} setFormData={setFormData} allowedViews={effLayout.allowed_views} />,
                form_proposal: <ProposalWidget formData={formData} setFormData={setFormData} isAdmin={isAdmin} result={result} />,
                form_project: <ProjectWidget formData={formData} setFormData={setFormData} equipment={data.equipment} licenses={data.licenses} clientSpecialFees={data.clientSpecialFees} clients={data.clients} results={results} />,
                form_benefits: formData.business_type === "perm"
                  ? <EmptyPermBenefitsWidget formData={formData} setFormData={setFormData} />
                  : (formData.regime_type === "contract"
                    ? <CombinedBenefitsWidget formData={formData} setFormData={setFormData} customBenefits={data.customBenefits} benefitSettings={data.benefitSettings} />
                    : <EmptyBenefitsWidget regime={formData.regime_type} formData={formData} setFormData={setFormData} />),
                results_score: can("view_business_decision") ? <ScoreWidget result={result} results={results} /> : null,
                results_snapshot: (can("view_company_cost") || can("view_margin")) ? <BusinessSnapshotWidget result={result} formData={formData} marginColors={marginColors} snapshotColors={snapshotColors} /> : null,
                results_company: can("view_company_cost") || can("view_margin") ? <CompanyResultsWidget result={result} results={results} formData={formData} marginColors={marginColors} /> : null,
                results_employee: can("view_net_salary") ? <EmployeeResultsWidget result={result} formData={formData} /> : null,
                results_premium: can("view_premium") ? <PremiumWidget results={results} /> : null,
                results_cashflow: (can("view_company_cost") || can("view_margin")) ? <CashflowChartWidget result={result} formData={formData} /> : null,
                actions_share: (can("view_proposals") || can("export_pdf")) ? <ShareWidget onShare={shareWithResults.handleSaveAndShare} onGeneratePDF={() => setShowProposalEditor(true)} hasResults={!!result} /> : null,
              }}
            />
          </div>
        )}
      </main>

      {/* Share Dialog */}
      <Dialog open={shareWithResults.showShareDialog} onOpenChange={shareWithResults.setShowShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-sans font-bold text-xl flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Guardar e Partilhar Simulação
            </DialogTitle>
          </DialogHeader>

          {!shareWithResults.shareUrl ? (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  O link será gerado com base nas iniciais do nome e data de nascimento.
                  <br />
                  <span className="font-mono text-xs">Ex: João Pedro Silva + 15/03 = JPS0315</span>
                </p>
              </div>

              {(!form.consultantName || (!form.consultantNif && !form.consultantEmail)) && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-700">
                    Preencha o nome e NIF (ou Email) do consultor no formulário para gerar o link.
                  </p>
                </div>
              )}

              {form.consultantName && (form.consultantNif || form.consultantEmail) && (
                <div className="p-3 bg-brand-50 rounded-lg border border-brand-200">
                  <p className="text-sm text-brand-700">
                    <strong>Consultor:</strong> {form.consultantName}<br />
                    {form.consultantNif && <><strong>NIF:</strong> {form.consultantNif}<br /></>}
                    {form.consultantEmail && <><strong>Email:</strong> {form.consultantEmail}<br /></>}
                    <strong>ID do Link:</strong> <span className="font-mono">{form.consultantNif || form.consultantEmail}</span>
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Input
                  placeholder="Ex: Proposta inicial para o projeto X"
                  value={shareWithResults.shareData.notes}
                  onChange={(e) => shareWithResults.setShareData({ ...shareWithResults.shareData, notes: e.target.value })}
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => shareWithResults.setShowShareDialog(false)}>Cancelar</Button>
                <Button
                  onClick={shareWithResults.handleSaveAndShare}
                  disabled={shareWithResults.savingShare || !form.consultantName || !form.consultantNif}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="save-share-btn"
                >
                  {shareWithResults.savingShare ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                  Guardar e Obter Link
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-brand-50 border border-brand-200 rounded-lg">
                <div className="flex items-center gap-2 text-brand-700 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Simulação guardada!</span>
                </div>
                <p className="text-sm text-brand-600">O link abaixo pode ser partilhado e permite editar os valores.</p>
              </div>
              <div className="space-y-2">
                <Label>Link de Partilha</Label>
                <div className="flex gap-2">
                  <Input value={shareWithResults.shareUrl} readOnly className="font-mono text-sm" />
                  <Button variant="outline" onClick={shareWithResults.copyShareUrl}><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => window.open(shareWithResults.shareUrl, '_blank')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Abrir Link
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-brand-600 text-brand-600 hover:bg-brand-50"
                  onClick={() => {
                    // Extrai o ID da URL para o dialog de email
                    const m = (shareWithResults.shareUrl || "").match(/\/proposal\/([^?#]+)/);
                    const simId = m ? m[1] : "";
                    shareWithResults.openEmailDialog?.(simId, form.consultantEmail || "");
                  }}
                  data-testid="send-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar por Email
                </Button>
                <Button className="flex-1" onClick={shareWithResults.closeShareDialog}>Fechar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog
        open={shareWithResults.emailDialog?.open || false}
        onOpenChange={(o) => !o && shareWithResults.closeEmailDialog?.()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-sans font-bold text-xl flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Enviar Proposta por Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <p className="text-xs text-slate-500">
              O email será enviado com base no template em <code>/admin/email-templates</code>,
              com o link da proposta. Envio atualmente <strong>MOCKADO</strong> (registado em log).
            </p>
            <div>
              <Label className="text-xs">Para</Label>
              <Input
                type="email"
                value={shareWithResults.emailDialog?.to || ""}
                onChange={(e) =>
                  shareWithResults.setEmailDialog?.((s) => ({ ...s, to: e.target.value }))
                }
                placeholder="candidato@email.pt"
                className="mt-1"
                data-testid="email-to-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={shareWithResults.closeEmailDialog}>
              Cancelar
            </Button>
            <Button
              onClick={shareWithResults.sendEmail}
              className="bg-brand-600 hover:bg-brand-700 text-white"
              disabled={shareWithResults.emailDialog?.sending}
              data-testid="confirm-send-email-btn"
            >
              {shareWithResults.emailDialog?.sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog
        open={shareWithResults.emailDialog?.open || false}
        onOpenChange={(o) => !o && shareWithResults.closeEmailDialog?.()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-sans font-bold text-xl flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Enviar Proposta por Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <p className="text-xs text-slate-500">
              O email será enviado com base no template em <code>/admin/email-templates</code>,
              com o link da proposta. Envio atualmente <strong>MOCKADO</strong> (registado em log).
            </p>
            <div>
              <Label className="text-xs">Para</Label>
              <Input
                type="email"
                value={shareWithResults.emailDialog?.to || ""}
                onChange={(e) =>
                  shareWithResults.setEmailDialog?.((s) => ({ ...s, to: e.target.value }))
                }
                placeholder="candidato@email.pt"
                className="mt-1"
                data-testid="email-to-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={shareWithResults.closeEmailDialog}>
              Cancelar
            </Button>
            <Button
              onClick={shareWithResults.sendEmail}
              className="bg-brand-600 hover:bg-brand-700 text-white"
              disabled={shareWithResults.emailDialog?.sending}
              data-testid="confirm-send-email-btn"
            >
              {shareWithResults.emailDialog?.sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Layout Builder Modal */}
      <Dialog open={showLayoutBuilder} onOpenChange={setShowLayoutBuilder}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Personalizar Layout do Simulador</DialogTitle>
          </DialogHeader>
          <LayoutBuilder
            getAuthHeaders={getAuthHeaders}
            mode="user"
            currentView={currentView}
            isAdminUser={isAdmin}
            onSaved={() => {
              setShowLayoutBuilder(false);
              effLayout.refresh();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Proposal PDF Editor */}
      <ProposalEditor
        open={showProposalEditor}
        onClose={() => setShowProposalEditor(false)}
        getAuthHeaders={getAuthHeaders}
        consultantName={form.consultantName}
        consultantFunction={form.consultantFunction}
        consultantNif={form.consultantNif}
        consultantEmail={form.consultantEmail}
        formData={formData}
        results={results}
      />
    </div>
  );
};

export default SimulatorPage;
